// Project status lifecycle (PRD §3.10, PE1, CM3).

import type { Iso } from "./dates";

export const STATUSES = ["prospect", "opportunity", "pending", "booked", "delivered", "lost", "cancelled"] as const;
export type ProjectStatus = (typeof STATUSES)[number];

/** Defaults only: every project can override its own probability. */
export const DEFAULT_PROBABILITY: Readonly<Record<ProjectStatus, number>> = {
  prospect: 0.1,
  opportunity: 0.25,
  pending: 0.75,
  booked: 1,
  delivered: 1,
  lost: 0,
  cancelled: 0,
};

/** "Closed" is a filter group, not a status. */
export const CLOSED_STATUSES: ReadonlySet<ProjectStatus> = new Set(["delivered", "lost", "cancelled"]);
export const isClosed = (status: ProjectStatus): boolean => CLOSED_STATUSES.has(status);

/** Lost and cancelled need a reason and a date, so win/loss can be reported. */
export const requiresReason = (status: ProjectStatus): boolean => status === "lost" || status === "cancelled";

/** Moving to booked takes Baseline 1 automatically (PP1). */
export const takesBaseline = (from: ProjectStatus, to: ProjectStatus): boolean => to === "booked" && from !== "booked";

export interface StatusFacts {
  status: ProjectStatus;
  /** Set when status is lost or cancelled. */
  closedOn?: Iso;
}

/**
 * Does a project's plan count toward capacity on `day`? Delivered and lost never do. A cancelled
 * project's weeks before its cancellation date stay (they happened); from that date on they are
 * released, which is the moment someone needs re-staffing (§3.10).
 */
export function countsTowardCapacity(p: StatusFacts, day: Iso, included: ReadonlySet<ProjectStatus>): boolean {
  if (p.status === "cancelled") return !!p.closedOn && day < p.closedOn;
  if (p.status === "delivered" || p.status === "lost") return false;
  return included.has(p.status);
}

export interface StatusStep {
  status: ProjectStatus;
  at: string;
  /** The effective day, when it differs from the day the change was made. */
  date?: Iso;
}
